/** @jsx jsx */
import React, { useCallback, type ReactNode } from 'react';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Form, { FormFooter, FormSection, Field, useFormState, Label } from '@atlaskit/form';
import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import Select, { type ValueType } from '@atlaskit/select';
import Link from '@atlaskit/link';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';
import SectionMessage from '@atlaskit/section-message';
import { fg } from '@atlassian/jira-feature-gating';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type {
	ApplicationLinkV3Data,
	ApplicationLinkV3Simple,
	ApplicationLinkV3Status,
	RestApplinkOAuthStatus,
	RestOAuthConfig,
} from '../../../common/entities.tsx';
import { ApplicationUrl, DisplayName, DisplayUrl } from '../../common/fields/index.tsx';
import {
	APPLINKS_LIST_PAGE_URL,
	APPLINKS_LIST_PAGE_URL_LEGACY,
} from '../../../common/constants.tsx';
import { putApplinkOAuth1Config, updateApplinkV3 } from '../../../rest/index.tsx';
import { lookupTypeFromV3Data, type TypeDefinition } from '../../../common/application-types.tsx';
import { getApplinksAdminUrl } from '../../../common/interchange.tsx';
import {
	AUTH_DISABLED,
	AUTH_OAUTH1,
	AUTH_OAUTH1_IMPERSONATION,
	AUTH_OAUTH2,
	getAuthLevel,
	type AuthType,
} from '../../../common/status/oauth1-match.tsx';
import commonMessages from '../../messages.tsx';
import statusMessages from '../../common/status/messages.tsx';
import messages from '../messages.tsx';
import { Arrows, type ArrowState } from './constants.tsx';

export const SUPPORTED_AUTH_TYPES: AuthType[] = [
	AUTH_OAUTH1,
	AUTH_OAUTH1_IMPERSONATION,
	AUTH_DISABLED,
];

type AuthOption = { label: string; value: AuthType };

const styles = cssMap({
	growField: {
		flexGrow: 1,
	},
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
	fieldNegativeMargin: {
		// Workaround for https://product-fabric.atlassian.net/browse/DSP-5423
		marginTop: token('space.negative.100'),
	},
	colDirection: {
		width: '20%',
	},
	colOutgoing: {
		width: '40%',
	},
	colIncoming: {
		width: '40%',
	},
});

export const matchOAuthConfigs = (
	localAuthLevel?: AuthType,
	remoteAuthLevel?: AuthType,
): ArrowState => {
	if (!localAuthLevel || !remoteAuthLevel) {
		return 'mismatch';
	}

	if (localAuthLevel === remoteAuthLevel) {
		if (localAuthLevel === AUTH_DISABLED) {
			return 'disabled';
		}
		return 'match';
	}
	return 'mismatch';
};

const ApplinksConnectedArrow = (props: { isIncoming: boolean; state: ArrowState }) => {
	const { formatMessage } = useIntl();

	const formatAltText = useCallback(() => {
		switch (props.state) {
			case 'match':
				return formatMessage(messages.arrowMatchHoverTextNonfinal);
			case 'mismatch':
				return formatMessage(messages.arrowMismatchHoverTextNonfinal);
			case 'disabled':
			default:
				return formatMessage(messages.arrowDisabledHoverTextNonfinal);
		}
	}, [formatMessage, props.state]);

	// Render the appropriate arrow icon based on the connection state
	return props.isIncoming ? (
		<Tooltip content={formatAltText()}>
			<img height="16px" src={Arrows.incoming[props.state]} alt={formatAltText()} />
		</Tooltip>
	) : (
		<Tooltip content={formatAltText()}>
			<img height="16px" src={Arrows.outgoing[props.state]} alt={formatAltText()} />
		</Tooltip>
	);
};

const buildLocalisedAuthTypes = (
	formatMessage: (desc: MessageDescriptor) => string,
	selectableOnly?: boolean,
) => {
	const oauthLabel = 'OAuth';
	const impersonationLabel = formatMessage(statusMessages.authTypeImpersonationNonfinal);
	const disabledLabel = formatMessage(statusMessages.authTypeDisabledNonfinal);
	const oauth2Label = 'OAuth 2.0';

	if (selectableOnly) {
		return [
			{ label: oauthLabel, value: AUTH_OAUTH1 },
			{ label: disabledLabel, value: AUTH_DISABLED },
		];
	}

	return [
		{ label: oauthLabel, value: AUTH_OAUTH1 },
		{ label: impersonationLabel, value: AUTH_OAUTH1_IMPERSONATION },
		{ label: disabledLabel, value: AUTH_DISABLED },
		{ label: oauth2Label, value: AUTH_OAUTH2 },
	];
};

const LocalAuthDetails = (props: {
	isIncoming: boolean;
	state: ArrowState;
	name: string;
	defaultOAuthConfig?: RestOAuthConfig;
}) => {
	const { formatMessage } = useIntl();

	const getAuthOptions = useCallback(() => {
		return buildLocalisedAuthTypes(formatMessage, true);
	}, [formatMessage]);

	const getDefaultOption = useCallback(() => {
		const options = buildLocalisedAuthTypes(formatMessage);
		const authLevel = getAuthLevel(props.defaultOAuthConfig);

		return options.find((option) => option.value === authLevel) || options[0];
	}, [formatMessage, props.defaultOAuthConfig]);

	return (
		<Inline space="space.200" alignBlock="center">
			<Box xcss={styles.growField}>
				<Box xcss={styles.fieldNegativeMargin}>
					<Field<ValueType<AuthOption>> name={props.name} defaultValue={getDefaultOption()}>
						{({ fieldProps }) => (
							<Select
								testId={`applinks-administration.ui.edit.oauth1.local-auth-select.${props.isIncoming ? 'incoming' : 'outgoing'}`}
								{...fieldProps}
								inputId={props.name}
								options={getAuthOptions()}
							/>
						)}
					</Field>
				</Box>
			</Box>
			<ApplinksConnectedArrow isIncoming={props.isIncoming} state={props.state} />
		</Inline>
	);
};

const RemoteAuthDetails = (props: {
	type: TypeDefinition;
	oauthConfig?: RestOAuthConfig;
	url: string;
}) => {
	const { formatMessage } = useIntl();

	const getAuthDescription = useCallback(() => {
		const options = buildLocalisedAuthTypes(formatMessage);
		const authLevel = getAuthLevel(props.oauthConfig);

		return options.find((option) => option.value === authLevel);
	}, [formatMessage, props.oauthConfig]);

	return (
		<Inline space="space.300">
			<Inline space="space.100">
				{props.type.icon}
				<Link appearance="subtle" href={props.url}>
					{props.type.name}
				</Link>
			</Inline>
			<Text>
				{getAuthDescription()?.label ?? formatMessage(statusMessages.authTypeUnknownNonfinal)}
			</Text>
		</Inline>
	);
};

const ConfigureAuthTable = (props: {
	applink: ApplicationLinkV3Data;
	localAuth?: RestApplinkOAuthStatus;
	remoteAuth?: RestApplinkOAuthStatus;
}) => {
	const { formatMessage } = useIntl();

	const formState = useFormState<OAuth1EditForm>({
		values: true,
	});

	const getApplinkType = useCallback(() => {
		return lookupTypeFromV3Data(props.applink);
	}, [props.applink]);

	const getOutgoingMatchState = useCallback(() => {
		return matchOAuthConfigs(
			formState?.values.outgoingAuth?.value ?? undefined,
			getAuthLevel(props.remoteAuth?.incoming),
		);
	}, [props.remoteAuth, formState?.values.outgoingAuth]);

	const getIncomingMatchState = useCallback(() => {
		return matchOAuthConfigs(
			formState?.values.incomingAuth?.value ?? undefined,
			getAuthLevel(props.remoteAuth?.outgoing),
		);
	}, [props.remoteAuth, formState?.values.incomingAuth]);

	const showOAuthImpersonationError = useCallback(() => {
		const isLocalAuthImpersonation =
			formState?.values.incomingAuth?.value === AUTH_OAUTH1_IMPERSONATION;
		const isRemoteAuthImpersonation =
			formState?.values.outgoingAuth?.value === AUTH_OAUTH1_IMPERSONATION;

		if (isLocalAuthImpersonation || isRemoteAuthImpersonation) {
			return (
				<SectionMessage
					title={formatMessage(messages.impersonationRemovedTitleNonfinal)}
					appearance="error"
				>
					{formatMessage(messages.impersonationRemovedDescriptionNonfinal, {
						link: (chunks: ReactNode[]) => (
							<Link
								href="https://confluence.atlassian.com/jirakb/end-of-support-for-2loi-application-links-950806698.html"
								target="_blank"
							>
								{chunks}
							</Link>
						),
					})}
				</SectionMessage>
			);
		}

		return null;
	}, [formState?.values.incomingAuth?.value, formState?.values.outgoingAuth?.value, formatMessage]);

	return (
		<Stack>
			{showOAuthImpersonationError()}
			<table>
				<colgroup>
					<col css={styles.colDirection} />
					<col css={styles.colOutgoing} />
					<col css={styles.colIncoming} />
				</colgroup>
				<thead>
					<tr>
						<th>{formatMessage(messages.authTableColumnDirectionHeadingNonfinal)}</th>
						<th>{formatMessage(messages.authTableColumnLocalAuthenticationHeadingNonfinal)}</th>
						<th>{formatMessage(messages.authTableColumnRemoteAuthenticationHeadingNonfinal)}</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td>
							<Label htmlFor="outgoingAuth">
								{formatMessage(messages.authTableRowDirectionOutgoingNonfinal)}
							</Label>
						</td>
						<td>
							<LocalAuthDetails
								isIncoming={false}
								state={getOutgoingMatchState()}
								name="outgoingAuth"
								defaultOAuthConfig={props.localAuth?.outgoing}
							/>
						</td>
						<td>
							<RemoteAuthDetails
								type={getApplinkType()}
								url={props.applink.displayUrl}
								oauthConfig={props.remoteAuth?.incoming}
							/>
						</td>
					</tr>
					<tr>
						<td>
							<Label htmlFor="incomingAuth">
								{formatMessage(messages.authTableRowDirectionIncomingNonfinal)}
							</Label>
						</td>
						<td>
							<LocalAuthDetails
								isIncoming
								state={getIncomingMatchState()}
								name="incomingAuth"
								defaultOAuthConfig={props.localAuth?.incoming}
							/>
						</td>
						<td>
							<RemoteAuthDetails
								type={getApplinkType()}
								url={props.applink.displayUrl}
								oauthConfig={props.remoteAuth?.outgoing}
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</Stack>
	);
};

type OAuth1EditForm = {
	displayName: string;
	rpcUrl: string;
	displayUrl: string;
	outgoingAuth: ValueType<AuthOption>;
	incomingAuth: ValueType<AuthOption>;
};

type ApplinksOAuth1EditPageContentsProps = {
	applink: ApplicationLinkV3Data;
	status: ApplicationLinkV3Status | null;
	onDisplayError: (err: string) => void;
};

const buildOAuthConfigFromOption = (option: ValueType<AuthOption>): RestOAuthConfig => {
	if (option?.value === AUTH_OAUTH1) {
		return {
			enabled: true,
			twoLoEnabled: true,
			twoLoImpersonationEnabled: false,
			oAuth2ThreeLoEnabled: false,
		};
	}
	if (option?.value === AUTH_OAUTH1_IMPERSONATION) {
		return {
			enabled: true,
			twoLoEnabled: true,
			twoLoImpersonationEnabled: true,
			oAuth2ThreeLoEnabled: false,
		};
	}
	return {
		enabled: false,
		twoLoEnabled: false,
		twoLoImpersonationEnabled: false,
		oAuth2ThreeLoEnabled: false,
	};
};

const ApplinksOAuth1EditPageContents = (props: ApplinksOAuth1EditPageContentsProps) => {
	const { push } = useRouterActions();

	const { formatMessage } = useIntl();

	const getListPageUrl = useCallback(() => {
		if (fg('applinks-933-experimental-list-page')) {
			return APPLINKS_LIST_PAGE_URL;
		}
		return APPLINKS_LIST_PAGE_URL_LEGACY;
	}, []);

	const onFormSubmit = useCallback(
		async (data: OAuth1EditForm) => {
			try {
				const supportedAuthTypes = buildLocalisedAuthTypes(formatMessage, true);
				const isOutgoingAuthSupported = supportedAuthTypes.some(
					(type) => type.value === data.outgoingAuth?.value,
				);
				const isIncomingAuthSupported = supportedAuthTypes.some(
					(type) => type.value === data.incomingAuth?.value,
				);

				if (!isOutgoingAuthSupported) {
					throw new Error(
						formatMessage(messages.unsupportedAuthenticationType, {
							invalidAuthType: data.outgoingAuth?.label,
						}),
					);
				}

				if (!isIncomingAuthSupported) {
					throw new Error(
						formatMessage(messages.unsupportedAuthenticationType, {
							invalidAuthType: data.incomingAuth?.label,
						}),
					);
				}

				const applinkUpdate: ApplicationLinkV3Simple = {
					id: props.applink.id,
					type: props.applink.type,
					system: props.applink.system,
					primary: props.applink.primary,
					isExtraAuthRequired: props.applink.isExtraAuthRequired,

					// Only change these properties:
					name: data.displayName,
					displayUrl: data.displayUrl,
					rpcUrl: data.rpcUrl,
				};

				await updateApplinkV3(props.applink.id, applinkUpdate);

				await putApplinkOAuth1Config(props.applink.id, {
					outgoing: buildOAuthConfigFromOption(data.outgoingAuth),
					incoming: buildOAuthConfigFromOption(data.incomingAuth),
				});

				await push(getListPageUrl());
			} catch (err) {
				const maybeError = err instanceof Error ? err : undefined;
				log.safeErrorWithoutCustomerData(
					'applinks.administration.edit.oauth1.submitEditForm',
					'Failed to update OAuth 1.0 applink',
					maybeError,
				);
				props.onDisplayError(
					maybeError ? maybeError.message : formatMessage(commonMessages.unknownErrorTextNonfinal),
				);
			}
		},
		[formatMessage, getListPageUrl, props, push],
	);

	return (
		<>
			<Form<OAuth1EditForm> onSubmit={onFormSubmit}>
				{({ formProps, submitting }) => (
					<form {...formProps}>
						<Stack space="space.200">
							<FormSection title={formatMessage(messages.linkDetailsSectionHeadingNonfinal)}>
								<Stack space="space.200" xcss={styles.formSectionInner}>
									<DisplayName name="displayName" defaultName={props.applink.name} />
									<DisplayUrl name="displayUrl" defaultUrl={props.applink.displayUrl} />
									<ApplicationUrl
										name="rpcUrl"
										defaultUrl={props.applink.rpcUrl}
										disabled={false}
									/>
								</Stack>
							</FormSection>
							<hr role="presentation" css={styles.dividerStyle} />
							<FormSection title={formatMessage(messages.connectionsTitleNonfinal)}>
								{props.status ? (
									<Stack space="space.200" xcss={styles.formSectionInner}>
										<ConfigureAuthTable
											applink={props.applink}
											localAuth={props.status.localAuthentication}
											remoteAuth={props.status.remoteAuthentication}
										/>
										<Box>
											<Link
												href={getApplinksAdminUrl(props.applink.displayUrl, props.applink.type)}
												target="_blank"
											>
												{formatMessage(messages.goToRemoteSiteLinkNonfinal, {
													displayUrl: props.applink.displayUrl,
												})}
											</Link>
										</Box>
									</Stack>
								) : (
									<Inline space="space.200" xcss={styles.formSectionInner}>
										<Spinner />
										<Text>{formatMessage(messages.applinkStatusLoadingTextNonfinal)}</Text>
									</Inline>
								)}
							</FormSection>

							<FormFooter>
								<ButtonGroup>
									<LinkButton href={getListPageUrl()} appearance="subtle">
										{formatMessage(messages.formCancelButtonTextNonfinal)}
									</LinkButton>
									<Button
										testId="applinks-administration.ui.edit.oauth1.submit-button"
										type="submit"
										appearance="primary"
										isLoading={submitting}
									>
										{formatMessage(messages.formSaveButtonTextNonfinal)}
									</Button>
								</ButtonGroup>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

export default ApplinksOAuth1EditPageContents;
